/**
 * Battle action log: server-authoritative. Opens a full drawer from a single button.
 */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BattleActionLogDrawer from './BattleActionLogDrawer';
import { ThumbnailWithLabel } from './BattleActionLogRow';
import { collectRecentBattleActionLogThumbnails } from './battleActionLogRecentThumbnails';
import {
  BATTLE_ACTION_LOG_BUTTON_PATH,
  BATTLE_ACTION_LOG_BUTTON_SURFACE_FILTER,
} from '../config/welcomeAssets';
import { resolvePublicAssetBackgroundUrl } from '../utils/resolvePublicAssetUrl';
import './BattleActionLog.css';

const HEADER_PREVIEW_THUMB_LIMIT = 3;

function BattleActionLog({
  actionLog = [],
  gameState,
  userId,
  opponentId,
  currentUsername = '',
  opponentUsername = '',
  isMobile = false,
  inlineInUserRow = false,
  fixedDesktopExpandedPosition = null,
  orderDesignScale = 1,
  unitRegistryRef = null
}) {
  const { t } = useTranslation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const headerPreviewThumbs = useMemo(
    () => collectRecentBattleActionLogThumbnails(actionLog, userId, HEADER_PREVIEW_THUMB_LIMIT),
    [actionLog, userId]
  );

  const headerPreviewSlots = useMemo(
    () => Array.from({ length: HEADER_PREVIEW_THUMB_LIMIT }, (_, slotIndex) => (
      headerPreviewThumbs[slotIndex] ?? null
    )),
    [headerPreviewThumbs]
  );

  return (
    <>
      <div
        className={[
          'battle-action-log-container',
          isMobile ? 'battle-action-log-container--mobile' : '',
          inlineInUserRow ? 'battle-action-log-container--inline-user-row' : '',
        ].filter(Boolean).join(' ')}
        aria-label={t('battleUI.actionHistory.title')}
      >
        <button
          type="button"
          className={[
            'battle-action-log-button',
            isMobile ? 'battle-action-log-button--mobile' : '',
          ].filter(Boolean).join(' ')}
          onClick={() => setDrawerOpen(true)}
          aria-expanded={drawerOpen}
          aria-haspopup="dialog"
          style={{
            '--battle-action-log-button-image': resolvePublicAssetBackgroundUrl(BATTLE_ACTION_LOG_BUTTON_PATH),
            '--battle-action-log-button-filter': BATTLE_ACTION_LOG_BUTTON_SURFACE_FILTER,
          }}
        >
          <span className="battle-action-log-button-inner">
            <span className="battle-action-log-button-label">
              {t('battleUI.actionHistory.drawerOpenButton')}
            </span>
            <div className="battle-action-log-header-thumbs" aria-hidden>
              {headerPreviewSlots.map((slot, slotIndex) => (
                <div
                  key={slot ? slot.entryKey : `battle-action-log-thumb-slot-${slotIndex}`}
                  className={[
                    'battle-action-log-header-thumb',
                    slot ? 'battle-action-log-header-thumb--order' : 'battle-action-log-header-thumb--empty',
                  ].join(' ')}
                >
                  {slot ? (
                    <ThumbnailWithLabel
                      orderLike={slot.descriptor.orderLike}
                      gameState={gameState}
                      unitRegistryRef={unitRegistryRef}
                      zoneName="tooltip"
                      showSkull={slot.descriptor.showSkull}
                      healthDelta={slot.descriptor.healthDelta}
                      intelDelta={slot.descriptor.intelDelta}
                      generalDelta={slot.descriptor.generalDelta}
                      compactHeaderPreview
                    />
                  ) : null}
                </div>
              ))}
            </div>
          </span>
        </button>
      </div>
      <BattleActionLogDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        actionLog={actionLog}
        gameState={gameState}
        userId={userId}
        opponentId={opponentId}
        currentUsername={currentUsername}
        opponentUsername={opponentUsername}
        isMobile={isMobile}
        fixedDesktopExpandedPosition={fixedDesktopExpandedPosition}
        orderDesignScale={orderDesignScale}
        unitRegistryRef={unitRegistryRef}
      />
    </>
  );
}

export default BattleActionLog;
